import React, { useState, useEffect } from "react";

const ReservationFilters = ({ reservations, onFilter }) => {
  const [tableFilter, setTableFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");

  const uniqueTables = [...new Set(reservations.flatMap(r => r.tables))].sort();

  const getPeriod = (time) => {
    if (!time) return null;
    const [h] = time.split(":").map(Number);
    if (h >= 8 && h < 12) return "jutro";
    if (h >= 12 && h < 20) return "poslijepodne";
    return "vecer";
  };

  useEffect(() => {
    let filtered = reservations;
    if (tableFilter) filtered = filtered.filter(r => r.tables.includes(tableFilter));
    if (periodFilter) filtered = filtered.filter(r => getPeriod(r.start_datetime) === periodFilter);
    onFilter(filtered);
  }, [tableFilter, periodFilter, reservations, onFilter]);

  return (
    <div className="flex gap-2 mt-3">
      <select
        value={tableFilter}
        onChange={(e) => setTableFilter(e.target.value)}
        className="flex-1 bg-gray-800 text-[#c1b428] border border-gray-600 rounded px-2 py-1 text-xs"
      >
        <option value="">Svi stolovi</option>
        {uniqueTables.map(t => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>

      <select
        value={periodFilter}
        onChange={(e) => setPeriodFilter(e.target.value)}
        className="flex-1 bg-gray-800 text-[#c1b428] border border-gray-600 rounded px-2 py-1 text-xs"
      >
        <option value="">Svi termini</option>
        <option value="jutro">☀️ Jutro</option>
        <option value="poslijepodne">🌤️ Poslijepodne</option>
        <option value="vecer">🌙 Večer</option>
      </select>
    </div>
  );
};


const ReservationsModal = ({ date, reservations, onClose }) => {
  const [filteredReservations, setFilteredReservations] = useState(reservations);

  useEffect(() => {
    setFilteredReservations(reservations);
  }, [reservations]);

  const today = new Date();
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  return (
    <div
      id="modalBackground"
      onClick={(e) => e.target.id === "modalBackground" && onClose()}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
    >
      <div className="bg-gray-900 border border-[#c1b428] rounded-xl shadow-2xl w-[520px] max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="p-4 border-b border-[#c1b428]">
          <h2 className="text-base font-bold text-center text-[#c1b428]">
            {isToday
              ? "Pregled današnjih rezervacija"
              : `Pregled rezervacija za ${date.toLocaleDateString("hr-HR")}`}
          </h2>
          <p className="text-center text-xs text-gray-400 mt-1">
            {reservations.length} rezervacija
          </p>
          <ReservationFilters reservations={reservations} onFilter={setFilteredReservations} />
        </div>

        {/* Lista */}
        <div className="overflow-y-auto flex-1 p-3 space-y-2 print-area">
        {filteredReservations.length > 0 ? (() => {
            const now = new Date();
            const upcoming = filteredReservations.filter(r => {
            if (!isToday || !r.start_datetime) return true;
            const [h, m] = r.start_datetime.split(":").map(Number);
            return (h * 60 + m) >= (now.getHours() * 60 + now.getMinutes());
            });
            const past = filteredReservations.filter(r => {
            if (!isToday || !r.start_datetime) return false;
            const [h, m] = r.start_datetime.split(":").map(Number);
            return (h * 60 + m) < (now.getHours() * 60 + now.getMinutes());
            });
            const allSorted = [...upcoming, ...past];

            // Grupiraj po 4 za ispis
            const pages = [];
            for (let i = 0; i < allSorted.length; i += 4) {
            pages.push(allSorted.slice(i, i + 4));
            }

            return pages.map((pageReservations, pageIdx) => (
            <div key={pageIdx} className="print-page">
                {pageReservations.map((r) => {
                const isPast = past.includes(r);
                return (
                    <div
                    key={r.id}
                    className={`print-card border rounded-lg p-3 text-xs ${
                        isPast ? "bg-gray-900 border-gray-700 opacity-40" : "bg-gray-800 border-gray-700"
                    }`}
                    >
                    {/* Prikaz na ekranu */}
                    <div className="screen-only">
                        <div className="flex justify-between items-center mb-1">
                        <span className={`font-bold px-2 py-0.5 rounded text-xs ${
                            isPast ? "bg-gray-600 text-gray-300" : "bg-[#c1b428] text-black"
                        }`}>
                            {r.tables.join(", ")}
                        </span>
                        <span className="text-gray-300 font-semibold">
                            {r.start_datetime} – {r.end_datetime}
                            {r.shift && <span className="ml-1 text-[#c1b428]">({r.shift})</span>}
                        </span>
                        </div>
                        <div className="flex justify-between items-center">
                        <span className="text-white font-semibold text-sm">{r.guest_name}</span>
                        <span className="text-gray-400">👥 {r.guests} {r.guests === 1 ? "osoba" : "osobe"}</span>
                        </div>
                        {r.note && (
                        <div className="mt-1 pt-1 border-t border-gray-700 text-gray-400 italic">
                            📝 {r.note}
                        </div>
                        )}
                    </div>

                    {/* Prikaz na ispisu */}
                    <div className="print-only">
                        <div className="print-guest-name">{r.guest_name}</div>
                        <div className="print-details">
                        {r.tables.join(", ")} &nbsp;|&nbsp; {r.start_datetime} – {r.end_datetime}
                        {r.shift && ` (${r.shift})`}
                        &nbsp;|&nbsp; 👥 {r.guests}
                        </div>
                        {r.note && <div className="print-note">📝 {r.note}</div>}
                    </div>
                    </div>
                );
                })}
            </div>
            ));
        })() : (
            <p className="text-center text-gray-400 py-8">Nema rezervacija za odabrane filtere.</p>
        )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[#c1b428] flex justify-between no-print">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-700 text-[#c1b428] rounded hover:bg-gray-600 text-xs font-semibold"
          >
            Zatvori
          </button>
          <button
            onClick={() => {
                const printWindow = window.open("", "_blank");
                const reservationsHtml = [...document.querySelectorAll(".print-card")].map(card => {
                const name = card.querySelector(".print-guest-name")?.innerText || "";
                const details = card.querySelector(".print-details")?.innerText || "";
                const note = card.querySelector(".print-note")?.innerText || "";
                return `
                    <div class="card">
                    <div class="name">${name}</div>
                    <div class="details">${details}</div>
                    ${note ? `<div class="note">${note}</div>` : ""}
                    </div>
                `;
                }).join("");

                printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                    <head>
                    <meta charset="utf-8">
                    <title>Rezervacije</title>
                    <style>
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body { font-family: Arial, sans-serif; }
                        .card {
                        width: 100%;
                        height: 65mm;
                        padding: 5mm 12mm;
                        border-bottom: 1px dashed #000;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        page-break-inside: avoid;
                        break-inside: avoid;
                        }
                        .name {
                        font-size: 22pt;
                        font-weight: bold;
                        margin-bottom: 4mm;
                        }
                        .details {
                        font-size: 12pt;
                        color: #333;
                        }
                        .note {
                        font-size: 10pt;
                        color: #666;
                        margin-top: 3mm;
                        font-style: italic;
                        }
                    </style>
                    </head>
                    <body>
                    ${reservationsHtml}
                    </body>
                </html>
                `);
                printWindow.document.close();
                printWindow.focus();
                setTimeout(() => {
                printWindow.print();
                printWindow.close();
                }, 500);
            }}
            className="px-4 py-1.5 bg-[#c1b428] text-black rounded font-bold hover:bg-yellow-500 text-xs"
            >
            Ispis
          </button>
        </div>

      </div>
    </div>
  );
};

export default ReservationsModal;